import { Box, Paper, Stack, Typography } from "@mui/material";
import {
    WorkOutline,
    Category,
    PlayCircleOutline,
    StopCircle,
} from "@mui/icons-material";
import { ValidationProcessStatusPill } from "./ValidationProcessStatusPill";
import { ProjectProcess } from "../../projectProcess/ProjectProcessApiClient";
import { ValidationScenarioLabel } from "./ValidationScenarioLabel";
import { Localized } from "@fluent/react";
import { ProjectProcessTypeLabel } from "./ProjectProcessTypeLabel";
import { StructureName } from "../../../device/Structure";


const dateFmt = new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'medium',
});

export type NullableDate = Date | string | null | undefined;

export function formatDate(value: NullableDate): string {
    if (!value) return '-';
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? '-' : dateFmt.format(d);
}

type ValidationProcessHeader = {
    process: ProjectProcess
};

export const ValidationProcessHeader: React.FC<ValidationProcessHeader> = ({
    process
}) => {
    return (
        <Paper variant="outlined" sx={{ p: 0, mb: 1.5, position: "relative", overflow: "hidden" }}>
            <Box
                sx={{
                    bgcolor: (t) => (t.palette.mode === "dark" ? "grey.900" : "grey.100"),
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 1.25,
                    py: 1.25,
                    borderTopLeftRadius: 1,
                    borderTopRightRadius: 1,
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ md: "center" }}
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{ minWidth: 0 }}
                >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ minWidth: 0, flex: 1 }}>
                        <Stack sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: ".02em" }}>
                                <Localized id="process-no">overview</Localized>
                            </Typography>
                            <Box
                                sx={{
                                    mt: 0.25,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    bgcolor: "background.paper",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    fontWeight: 800,
                                    whiteSpace: "nowrap",
                                    maxWidth: 240,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                                title={process?.no ?? ""}
                            >
                                {process?.no ?? "—"}
                            </Box>
                        </Stack>

                        {/* Tytuł */}
                        <Stack sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: ".02em" }}>
                                <Localized id="process-name">Tytuł</Localized>
                            </Typography>
                            <Box
                                sx={{
                                    mt: 0.25,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    bgcolor: "background.paper",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    fontWeight: 800,
                                    maxWidth: "100%",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={process?.name ?? ""}
                            >
                                {process?.name ?? "—"}
                            </Box>
                        </Stack>
                    </Stack>
                    <Stack sx={{ minWidth: 0 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, letterSpacing: ".02em" }}
                        >
                            Struktura
                        </Typography>
                        <Box
                            sx={{
                                mt: 0.25,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                            title={process.structureId != null ? String(process.structureId) : ""}
                        >
                            <StructureName structureId={String(process.structureId)} />
                        </Box>
                    </Stack>
                    <Stack alignItems={{ xs: "flex-start", md: "flex-start" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: ".02em" }}>
                            <Localized id="status">Status</Localized>
                        </Typography>
                        <Box sx={{ mt: 0.25 }}>
                            <ValidationProcessStatusPill status={process.status} />
                        </Box>
                    </Stack>
                </Stack>
            </Box>


            <Box sx={{ px: 1.25, py: 1.25 }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems="flex-start"
                >
                    {/* Lewa kolumna: typ + scenariusz */}
                    <Stack spacing={1} flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <WorkOutline fontSize="small" />
                            <Typography variant="body2" noWrap title={process.processType}>
                                <strong><Localized id="process-type">process-type</Localized>:</strong>&nbsp;
                                <ProjectProcessTypeLabel value={process.processType} />
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <Category fontSize="small" />
                            <Typography variant="body2" noWrap title={process.scenario}>
                                <strong><Localized id="process-scenario">_scenario</Localized>:</strong>&nbsp;
                                <ValidationScenarioLabel value={process.scenario} />
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* Środkowa kolumna: daty */}
                    <Stack spacing={1} flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <PlayCircleOutline fontSize="small" />
                            <Typography variant="body2">
                                <strong><Localized id="process-start-date">_start</Localized>:</strong>&nbsp;
                                {formatDate(process?.startDate)}
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <StopCircle fontSize="small" />
                            <Typography variant="body2">
                                <strong><Localized id="process-end-date">_end</Localized>:</strong>&nbsp;
                                {formatDate(process?.finishDate)}
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* Prawa kolumna: ostatni komentarz akcji */}
                    {process.lastActionComment && (
                        <Stack spacing={0.5} flex={1} sx={{ minWidth: 0 }}>

                            <Box
                                sx={(t) => ({
                                    mt: 0.25,
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: 1,
                                    bgcolor:
                                        t.palette.mode === "dark"
                                            ? t.palette.grey[900]
                                            : t.palette.grey[50],
                                    border: `1px dashed ${t.palette.divider}`,
                                    maxWidth: "100%",
                                    overflow: "hidden",
                                })}
                            >
                                <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{
                                        textAlign: "right",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        fontStyle: "italic",
                                    }}
                                    title={process.lastActionComment || undefined}
                                >
                                    {process.lastActionComment}
                                </Typography>
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </Box>

        </Paper>
    );
};
